import EndGameModal from 'components/modals/EndGameModal/EndGameModal';
import { DelayNextGameModal } from 'components/modals/SkipGameModal/DelayNextGameModal';
import { ServiceTypes } from 'constants/ServiceTypes';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SessionMode } from 'enums/SessionMode';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FeatureConfigService } from 'services/FeatureConfigService';
import { useInjection } from 'services/ioc/ioc.react';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { ReactComponent as CloseIcon } from './closeIcon.svg';
import {
  Buttons,
  Content,
  Controls,
  Message,
  NotificationCounter,
  NotificationWrapper,
  StyledButton,
} from './UpcomingGameNotificationStyles';

dayjs.extend(utc);

interface IProps {
  onDissmissCallback: () => void;
  onConfirmCallback: () => void;
  externalSessionId: string;
  armingTime: string;
  timeRange: number;
  sessionId: string;
}

const SAFE_DELAY = 10;

const UpcomingGameNotification: React.FC<IProps> = observer((props: IProps) => {
  const [isLoading, setIsLoading] = useState<boolean>();
  const { onDissmissCallback, onConfirmCallback, externalSessionId, armingTime, timeRange, sessionId } = props;
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);
  const history = useHistory();

  const delayNextGameHandler = () => {
    try {
      setIsLoading(true);
      onConfirmCallback();
    } finally {
      setIsLoading(false);
    }
  };
  const onHandleNotificationClicked = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const [time, setTime] = useState(dayjs.utc(armingTime).diff(dayjs.utc(), 'seconds') - SAFE_DELAY);
  let interval: ReturnType<typeof setInterval>;

  useEffect(() => {
    interval = setInterval(() => {
      setTime(dayjs.utc(armingTime).diff(dayjs.utc(), 'seconds') - SAFE_DELAY);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (time <= 0) {
      onDissmissCallback();
    }
  }, [time]);

  useEffect(() => {
    if (sessionId === sessionStore.currentSessionMetadata.gameScheduleReference?.id) {
      onDissmissCallback();
    }
  }, [sessionStore.currentSessionMetadata]);

  const ignoreNotificationHandler = () => {
    if (time < SessionStore.SECOND_THRESHOLD_GAME_NOTIFICATION_TIME) {
      sessionStore.dismissUpcomingSessionDialog(sessionId);
    } else {
      sessionStore.temporaryDismissNotification(sessionId);
    }

    onDissmissCallback();
  };

  const [isEndGameModalOpen, setIsEndGameModalOpen] = useState(false);
  const [isSkipGameModalOpen, setIsSkipGameModalOpen] = useState(false);

  const handleOnEndGameCallback = async (): Promise<void> => {
    const isSuccess = await sessionStore.setCurrentSessionMode({
      ...sessionStore.currentSessionMode,
      sessionMode: SessionMode.SessionEnded,
    });
    if (isSuccess) {
      history.push(`/review/${sessionStore.currentSessionMode.sessionId}`);
    } else {
      notificationStore.addNotification({
        id: 'error-changing-session-mode',
        message: 'Error changing session mode',
        variant: 'error',
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('error-changing-session-mode');
        },
      });
    }
    setIsEndGameModalOpen(false);
    onDissmissCallback();
  };

  useEffect(() => {
    if (sessionStore.currentSessionMetadata?.sessionModeState?.state === SessionMode.SessionEnded) {
      onDissmissCallback();
    }
  }, [sessionStore.currentSessionMetadata.sessionModeState]);

  const featureConfigService = useInjection<FeatureConfigService>(ServiceTypes.FeatureConfigService);
  const { isOperatorlessTaggingEnabled } = featureConfigService.getConfig();

  return (
    <NotificationWrapper onClick={(event) => onHandleNotificationClicked(event)} data-testid='upcoming-game-notification'>
      <Content>
        <Message>
          <NotificationCounter>{`${
            isOperatorlessTaggingEnabled
              ? `${`Another game is starting soon. This session will end in ${dayjs(time * 1000).minute()} min`}.`
              : `Another game is starting soon. Unless you delay the next game, this session will end in ${dayjs(time * 1000).minute()} min`
          }`}</NotificationCounter>
        </Message>
        <Controls>
          <Buttons className='buttons'>
            <StyledButton
              label='End this game'
              intent='tertiary'
              onClick={() => setIsEndGameModalOpen(true)}
              width={'118px'}
              data-testid='stay-here-btn'
            />
            {!isOperatorlessTaggingEnabled && (
              <StyledButton
                label={`Delay next game`}
                intent='tertiary'
                onClick={() => {
                  !isLoading && setIsSkipGameModalOpen(true);
                }}
                width={'124px'}
                data-testid='skip-next-game-btn'
              />
            )}
          </Buttons>
          <CloseIcon onClick={ignoreNotificationHandler} />
        </Controls>
      </Content>
      <DelayNextGameModal
        dismissCallback={() => setIsSkipGameModalOpen(false)}
        open={isSkipGameModalOpen}
        delayNextGameHandler={delayNextGameHandler}
      />
      <EndGameModal
        open={isEndGameModalOpen}
        dismissCallback={() => setIsEndGameModalOpen(false)}
        endGameCallback={handleOnEndGameCallback}
      />
    </NotificationWrapper>
  );
});

export default UpcomingGameNotification;
